import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { onboardingUi } from '../i18n/onboardingUi.js';
import { t } from '../i18n/translate.js';

const PAD = 8;
const TOOLTIP_GAP = 14;
const ARROW = 10;

function revealCollapsedAncestors(el, revealed) {
  let node = el.parentElement;
  while (node) {
    if (node.hasAttribute('hidden') && node.id && !revealed.has(node.id)) {
      const trigger = document.querySelector(`[aria-controls="${CSS.escape(node.id)}"]`);
      if (trigger instanceof HTMLElement && trigger.getAttribute('aria-expanded') === 'false') {
        revealed.add(node.id);
        trigger.click();
      }
    }
    node = node.parentElement;
  }
}

/**
 * @param {string | null | undefined} selector
 * @param {number} timeoutMs
 */
function waitForElement(selector, timeoutMs = 6000) {
  if (!selector) {
    return Promise.resolve(null);
  }
  const existing = document.querySelector(selector);
  if (existing) {
    return Promise.resolve(existing);
  }
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) {
        resolve(el);
        return;
      }
      if (Date.now() - start > timeoutMs) {
        resolve(null);
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

/**
 * @param {DOMRect} rect
 * @param {{ width: number, height: number }} tooltipSize
 * @param {'top' | 'bottom' | 'left' | 'right' | 'center'} prefer
 */
function placeTooltip(rect, tooltipSize, prefer = 'bottom') {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 12;

  if (prefer === 'center' || !rect.width) {
    return {
      top: Math.max(margin, (vh - tooltipSize.height) / 2),
      left: Math.max(margin, (vw - tooltipSize.width) / 2),
      arrowSide: null,
      arrowTop: 0,
      arrowLeft: 0,
    };
  }

  const candidates = [prefer, 'bottom', 'top', 'right', 'left'].filter((v, i, a) => a.indexOf(v) === i);

  for (const side of candidates) {
    let top = 0;
    let left = 0;
    if (side === 'bottom') {
      top = rect.bottom + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipSize.width / 2;
    } else if (side === 'top') {
      top = rect.top - TOOLTIP_GAP - tooltipSize.height;
      left = rect.left + rect.width / 2 - tooltipSize.width / 2;
    } else if (side === 'right') {
      top = rect.top + rect.height / 2 - tooltipSize.height / 2;
      left = rect.right + TOOLTIP_GAP;
    } else {
      top = rect.top + rect.height / 2 - tooltipSize.height / 2;
      left = rect.left - TOOLTIP_GAP - tooltipSize.width;
    }
    left = Math.min(Math.max(margin, left), vw - tooltipSize.width - margin);
    top = Math.min(Math.max(margin, top), vh - tooltipSize.height - margin);

    let arrowTop = 0;
    let arrowLeft = 0;
    if (side === 'bottom') {
      arrowTop = top - ARROW;
      arrowLeft = rect.left + rect.width / 2 - ARROW;
    } else if (side === 'top') {
      arrowTop = top + tooltipSize.height;
      arrowLeft = rect.left + rect.width / 2 - ARROW;
    } else if (side === 'right') {
      arrowTop = rect.top + rect.height / 2 - ARROW;
      arrowLeft = left - ARROW;
    } else if (side === 'left') {
      arrowTop = rect.top + rect.height / 2 - ARROW;
      arrowLeft = left + tooltipSize.width;
    }
    return { top, left, arrowSide: side, arrowTop, arrowLeft };
  }

  return { top: margin, left: margin, arrowSide: null, arrowTop: 0, arrowLeft: 0 };
}

/**
 * @param {{
 *   active: boolean,
 *   steps: Array<{ selector?: string | null, title: object, body: object, side?: string }>,
 *   stepIndex: number,
 *   locale: string,
 *   onNext: () => void,
 *   onPrev: () => void,
 *   onSkip: () => void,
 *   onFinish: () => void,
 * }} props
 */
export function GuidedTour({
  active,
  steps,
  stepIndex,
  locale,
  onNext,
  onPrev,
  onSkip,
  onFinish,
}) {
  const tooltipRef = useRef(null);
  const [spotlight, setSpotlight] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({
    top: 0,
    left: 0,
    arrowSide: null,
    arrowTop: 0,
    arrowLeft: 0,
  });
  const [ready, setReady] = useState(false);

  const step = steps[stepIndex];
  const isLast = stepIndex >= steps.length - 1;
  const isFirst = stepIndex === 0;

  const labels = {
    next: t(onboardingUi.next, locale),
    back: t(onboardingUi.back, locale),
    skip: t(onboardingUi.skip, locale),
    finish: t(onboardingUi.finish, locale),
  };

  useLayoutEffect(() => {
    if (!active || !step) {
      setReady(false);
      return undefined;
    }

    let cancelled = false;
    const revealed = new Set();

    const update = async () => {
      setReady(false);
      let el = null;
      if (step.selector) {
        el = await waitForElement(step.selector);
        if (cancelled) {
          return;
        }
        if (el) {
          revealCollapsedAncestors(el, revealed);
          await new Promise((r) => requestAnimationFrame(r));
          el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
          await new Promise((r) => setTimeout(r, 280));
        }
      }

      if (cancelled) {
        return;
      }

      const rect = el
        ? el.getBoundingClientRect()
        : { top: 0, left: 0, width: 0, height: 0, right: 0, bottom: 0 };

      if (el && rect.width > 0) {
        setSpotlight({
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
        });
      } else {
        setSpotlight(null);
      }

      const tipEl = tooltipRef.current;
      const tipSize = tipEl
        ? { width: tipEl.offsetWidth, height: tipEl.offsetHeight }
        : { width: 320, height: 160 };

      const pos = placeTooltip(rect, tipSize, step.selector ? step.side || 'bottom' : 'center');
      setTooltipPos(pos);
      setReady(true);
    };

    update();
    const onResize = () => update();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [active, step, stepIndex]);

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (isLast) {
          onFinish();
        } else {
          onNext();
        }
      } else if (e.key === 'ArrowLeft' && !isFirst) {
        onPrev();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, isFirst, isLast, onFinish, onNext, onPrev, onSkip]);

  if (!active || !step) {
    return null;
  }

  return createPortal(
    <div className="onboarding-root" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
      {!spotlight ? <div className="onboarding-backdrop" aria-hidden /> : null}
      {spotlight ? (
        <div
          className="onboarding-spotlight"
          style={{
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
          }}
        />
      ) : null}
      {tooltipPos.arrowSide ? (
        <div
          className={`onboarding-arrow onboarding-arrow--${tooltipPos.arrowSide}`}
          style={{ top: tooltipPos.arrowTop, left: tooltipPos.arrowLeft }}
        />
      ) : null}
      <div
        ref={tooltipRef}
        className="onboarding-tooltip"
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          visibility: ready ? 'visible' : 'hidden',
        }}
      >
        <h3 id="onboarding-title">{t(step.title, locale)}</h3>
        <p>{t(step.body, locale)}</p>
        <div className="onboarding-tooltip-footer">
          <span className="onboarding-progress">
            {stepIndex + 1} / {steps.length}
          </span>
          <div className="onboarding-actions">
            <button type="button" className="btn-secondary" onClick={onSkip}>
              {labels.skip}
            </button>
            {!isFirst ? (
              <button type="button" className="btn-secondary" onClick={onPrev}>
                {labels.back}
              </button>
            ) : null}
            {isLast ? (
              <button type="button" className="btn-primary" onClick={onFinish}>
                {labels.finish}
              </button>
            ) : (
              <button type="button" className="btn-primary" onClick={onNext}>
                {labels.next}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
